import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  handleUpdateFields,
  getCachbackItem,
  createCashback,
  updateCashback,
} from "store/liveOps/casbacks/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalCasbackTabs } from "configs/types";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import FilterTabs from "components/FilterTabs";
import CashbackGamesConfigTab from "../tabs/cashbackGames/CashbackGamesConfigTab";

import { Modal } from "components/Modal/Modal";
import styles from "./LiveOpsCashbacksEditModal.module.scss";
import { ImageUpload } from "components/imageUpload";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { formatTime, useTimeFormat } from "helpers/timeFormat";
import TextEditor from "components/wysiwyg/Wysiwyg";
import {
  CashbackBonusTemplate,
  getCashbackRewardBonuses,
} from "services/cashbackBonusService";
import { usePermissionHelper } from "helpers/usePermissionHelper";

type BonusOption = {
  value: number;
  label: string;
  img?: string;
};

const hours = Array.from(
  { length: 24 },
  (_, i) => `${i.toString().padStart(2, "0")}:00`,
);

const LiveOpsCashbacksEditModal: React.FC = () => {
  const { t } = useTranslation();
  const timeFormat = useTimeFormat();
  const dispatch = useAppDispatch();
  const formErrors = useFormErrors();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isItemFetched = useAppSelector(
    (store) => store.cashbacks.isItemFetched,
  );
  const payload = useAppSelector((store) => store.cashbacks.payload);
  const currenciesList = useAppSelector((store) => store.settings.currencies);
  const isItemFetching = useAppSelector(
    (store) => store.cashbacks.isItemFetching,
  );

  const [activeCurrency, setActiveCurrency] = useState<string>(
    currenciesList[0],
  );
  const [bonusOptions, setBonusOptions] = useState<BonusOption[]>([]);
  const [isBonusesLoading, setIsBonusesLoading] = useState(false);
  const [isBonusesError, setIsBonusesError] = useState(false);
  const [bonusReloadKey, setBonusReloadKey] = useState(0);

  const [tabs] = useState<ModalCasbackTabs[]>([
    ModalCasbackTabs.main,
    ModalCasbackTabs.config,
    // ModalCasbackTabs.settings,
  ]);
  const [activeTab, setActiveTab] = useState<ModalCasbackTabs>(
    ModalCasbackTabs.main,
  );
  const hourOptions = useMemo(
    () =>
      hours.map((hour) => ({
        value: hour,
        label: formatTime(hour, timeFormat),
      })),
    [timeFormat],
  );

  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);
  const permissions = usePermissionHelper();
  const canCreate = permissions?.create ?? false;
  const canUpdate = permissions?.update ?? false;
  const canEdit = isEdit ? canUpdate : canCreate;

  useEffect(() => {
    if (entityId !== null && !isItemFetched) {
      dispatch(getCachbackItem(entityId ?? ""));
    }
  }, [searchParams, isItemFetched]);

  useEffect(() => {
    let isMounted = true;

    const loadRewardBonuses = async () => {
      setIsBonusesLoading(true);
      setIsBonusesError(false);

      try {
        const items: CashbackBonusTemplate[] =
          await getCashbackRewardBonuses(activeCurrency);

        if (isMounted) {
          setBonusOptions(
            items.map((bonus) => ({
              value: bonus.id,
              label: bonus.title,
              img: bonus.image,
            })),
          );
        }
      } catch {
        if (isMounted) {
          setIsBonusesError(true);
          dispatch(
            showNotification({
              title: t("error"),
              message: t("entityUpdateError"),
              type: NotificationTypes.error,
            }),
          );
        }
      } finally {
        if (isMounted) {
          setIsBonusesLoading(false);
        }
      }
    };

    loadRewardBonuses();

    return () => {
      isMounted = false;
    };
  }, [activeCurrency, bonusReloadKey, dispatch, t]);

  const selectedBonusId =
    payload?.bonusTemplateId != null && Number(payload.bonusTemplateId) > 0
      ? Number(payload.bonusTemplateId)
      : undefined;

  const displayBonusOptions = useMemo(() => {
    if (
      !selectedBonusId ||
      bonusOptions.some(
        (option) => String(option.value) === String(selectedBonusId),
      )
    ) {
      return bonusOptions;
    }

    return [
      {
        value: selectedBonusId,
        label: isBonusesLoading ? t("loading") : t("inactiveRewardBonus"),
      },
      ...bonusOptions,
    ];
  }, [bonusOptions, isBonusesLoading, selectedBonusId, t]);

  const isLoaded = () => {
    if (entityId !== null && !isItemFetched) {
      return false;
    }

    return true;
  };

  const loaded = isLoaded();

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    if (!canEdit) return;
    formErrors.clearFieldError(key);
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleApply = async () => {
    if (!canEdit) return;
    formErrors.resetErrors();
    if (activeTab === ModalCasbackTabs.config) {
      const activeItem = payload?.configs?.filter(
        (el) => el.currency === activeCurrency,
      )[0];

      try {
        await instance.put(`${apiRoutes.cashbackUpdateConfig}/${entityId}`, {
          currency: activeItem?.currency,
          max: Number(activeItem?.max),
          min: Number(activeItem?.min),
          percentage:
            activeItem?.percentage == null ? "" : String(activeItem.percentage),
          payoutCurrency: activeItem?.payoutCurrency ?? "",
        });
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({ error, t, dispatch, formErrors });
      }
    }
    if (payload !== undefined && activeTab === ModalCasbackTabs.main) {
      try {
        if (entityId) {
          const result = await dispatch(updateCashback(payload)).unwrap();

          if (result === true) {
            dispatch(
              showNotification({
                title: t("success"),
                message: t("entityUpdateSuccess"),
                type: NotificationTypes.success,
              }),
            );
          }
        } else {
          const result = await dispatch(createCashback(payload)).unwrap();

          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityCreateSuccess"),
              type: NotificationTypes.success,
            }),
          );

          navigate(
            `${location.pathname}?activeModal=cashback&entityId=${result}`,
          );
        }
      } catch (error) {
        handleApiError({ error, t, dispatch, formErrors });
      }
    }
  };

  useEffect(() => {
    formErrors.resetErrors();
  }, [activeCurrency, activeTab]);

  const renderTab = () => {
    if (activeTab === ModalCasbackTabs.config) {
      if (isBonusesLoading) {
        return <ModalTabSkeleton />;
      }

      return (
        <CashbackGamesConfigTab fieldErrors={formErrors.errors} clearFieldError={formErrors.clearFieldError}
          activeCurrency={activeCurrency}
          setActiveCurrency={setActiveCurrency}
          isReadOnly={!canEdit}
        />
      );
    }
    if (activeTab === ModalCasbackTabs.main) {
      return (
        <div className={`bonus-form-content ${styles.mainForm}`}>
          <div className="bonus-form-box">
            <div className="bonus-form-box--item">
              <InnerInput
                value={payload?.title ?? ""}
                placeholder={t("enterTitle")}
                label={t("title")}
                type="text"
                id="cashback-title"
                disabled={!canEdit}
                onChange={(e) => handleData("title", e)}
              />
            </div>
            <div
              className={`bonus-form-box--item bonus-form-box--row ${styles.levelRow}`}
            >
              <InnerInput
                hasError={Boolean(formErrors.errors?.["level"])}
                value={payload?.level?.toString() ?? ""}
                placeholder={t("level")}
                label={t("level")}
                type="number"
                id="cashback-level"
                disabled={!canEdit}
                onChange={(e) => handleData("level", e)}
              />
              <div className={styles.rewardBonus}>
                <DefaultDropdown
                  value={selectedBonusId ?? ""}
                  label={t("rewardBonus")}
                  placeholder={
                    isBonusesLoading ? t("loading") : t("selectRewardBonus")
                  }
                  options={displayBonusOptions}
                  loading={isBonusesLoading}
                  disabled={!canEdit || isBonusesLoading}
                  emptyText={t("noActiveCashbackBonuses")}
                  onSelect={(value) =>
                    handleData("bonusTemplateId", Number(value))
                  }
                />
                {isBonusesError && (
                  <button
                    type="button"
                    className="reward-bonus-retry"
                    onClick={() => setBonusReloadKey((key) => key + 1)}
                  >
                    {t("retry")}
                  </button>
                )}
              </div>
            </div>
            <div className={`bonus-form-box--item ${styles.descriptionField}`}>
              <InnerInput
                maxCodePoints={2500}
                value={payload?.description ?? ""}
                placeholder={t("enterText")}
                label={t("description")}
                type="textarea"
                id="cashback-description"
                disabled={!canEdit}
                onChange={(e) => handleData("description", e)}
              />
            </div>
            <div
              className={`bonus-form-box--item bonus-form-box--row ${styles.scheduleRow}`}
            >
              <FilterTabs
                value={payload?.type}
                isInner
                label={t("type")}
                disabled={!canEdit}
                onSelect={(e) => handleData("type", e)}
                options={[
                  {
                    value: "hourly",
                    label: t("hourly"),
                  },
                  {
                    value: "daily",
                    label: t("daily"),
                  },
                  {
                    value: "weekly",
                    label: t("weekly"),
                  },
                  {
                    value: "monthly",
                    label: t("monthly"),
                  },
                  {
                    value: "yearly",
                    label: t("yearly"),
                  },
                ]}
              />
              <DefaultDropdown
                value={payload?.checkingAt}
                onSelect={(e) => {
                  handleData("checkingAt", e);
                }}
                placeholder={t("checkingAt")}
                label={t("checkingAt")}
                options={hourOptions}
                disabled={!canEdit}
              />
            </div>
            <div className="bonus-form-box--item">
              <FilterTabs
                value={payload?.eventSource || ""}
                isInner
                label={t("eventSource")}
                disabled={!canEdit}
                onSelect={(value) => handleData("eventSource", value)}
                options={[
                  { value: "", label: t("wagerGames") },
                  { value: "ALL", label: t("all") },
                  { value: "CASINO", label: t("casino") },
                  { value: "SPORT", label: t("sport") },
                ]}
              />
            </div>
          </div>
          <div className="bonus-form-box">
            <div className="game-form-img game-form-img--game center">
              <div className={styles.imageLabel}>{t("image")}</div>
              <div className="game-item">
                <ImageUpload
                  image={payload?.image}
                  size="large"
                  onAddImage={(file) => {
                    handleData("image", "", file);
                  }}
                  isActive={Boolean(payload?.isActive)}
                  disabled={!canEdit}
                />
              </div>
              <div className="game-form-switcher">
                <SwitchBtn
                  activeTxt={t("active")}
                  notActiveTxt={t("hidden")}
                  isActive={!!payload?.isActive}
                  disabled={!canEdit}
                  handleClick={() =>
                    handleData(
                      "isActive",
                      payload?.isActive ? !payload?.isActive : true,
                    )
                  }
                />
              </div>
            </div>
            <div className={styles.extraFields}>
              <InnerInput
                hasError={Boolean(formErrors.errors?.["wagerMultiplier"])}
                value={payload?.wagerMultiplier ?? ""}
                placeholder={t("cashbackWager")}
                label={t("cashbackWager")}
                tooltipText={t("cashbackWager")}
                type="text"
                id="cashback-wager-multiplier"
                disabled={!canEdit}
                onChange={(value) => handleData("wagerMultiplier", value)}
              />
              <InnerInput
                hasError={Boolean(formErrors.errors?.["claimExpirationHours"])}
                value={payload?.claimExpirationHours?.toString() ?? ""}
                placeholder={t("claimPeriodInHours")}
                label={t("claimPeriodInHours")}
                tooltipText={t("claimPeriodTooltip")}
                tooltipPosition="right"
                type="number"
                min={0}
                id="cashback-claim-expiration-hours"
                disabled={!canEdit}
                onChange={(value) =>
                  handleData(
                    "claimExpirationHours",
                    value === "" ? "" : Number(value),
                  )
                }
              />
            </div>
          </div>
          <div
            className={`bonus-form-box--item html-editor ${styles.contentEditor}`}
          >
            <div className="label">{t("content")}</div>
            <TextEditor
              value={payload?.content ?? ""}
              disabled={!canEdit}
              onChange={(value) => handleData("content", value)}
            />
          </div>
        </div>
      );
    }
    return <div className="game-form-wrapper"></div>;
  };

  const handleTabClick = (tab: ModalCasbackTabs) => {
    if (!isEdit && tab !== ModalCasbackTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("cashback")}</Modal.Header>

      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
          />

          <Modal.Content className={styles.liveopsCashbacksContent}>
            <div className="bonus-content casback-content">{renderTab()}</div>
          </Modal.Content>

          {canEdit && (
            <Modal.Footer>
              {activeTab === ModalCasbackTabs.config &&
              isBonusesLoading ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />

                  <Button
                    isPrimary
                    isLoad={isItemFetching}
                    handleClick={handleApply}
                    txt={t("save")}
                  />
                </>
              )}
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default LiveOpsCashbacksEditModal;
